type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

const REASON_CODE_LABELS: Record<string, string> = {
  MatchedByKsef: 'Dopasowanie po numerze KSeF',
  MatchedByComposite: 'Dopasowanie po danych złożonych',
  MissingReferenceData: 'Brak danych referencyjnych',
  NoLocalMatch: 'Brak lokalnego dopasowania',
  SkippedManualFixed: 'Pominięto ręcznie naprawiony rekord',
  OverrideApplied: 'Zastosowano override manual-fixed',
  RetryEligibleFailed: 'Zlecenie importu kwalifikuje się do ponowienia (status: błąd)',
  ApproveEligibleRequiresReview:
    'Zlecenie importu kwalifikuje się do zatwierdzenia (wymaga weryfikacji)',
  StatusNotEligible: 'Status nie kwalifikuje się do akcji',
  MissingImportJob: 'Nie znaleziono zlecenia importu',
  OperationFailed: 'Akcja zakończona błędem',
  Unknown: 'Nieznany powód',
};

const REASON_CODE_COLORS: Record<string, ChipColor> = {
  MatchedByKsef: 'success',
  MatchedByComposite: 'success',
  MissingReferenceData: 'warning',
  NoLocalMatch: 'warning',
  SkippedManualFixed: 'warning',
  OverrideApplied: 'info',
  RetryEligibleFailed: 'info',
  ApproveEligibleRequiresReview: 'info',
  StatusNotEligible: 'warning',
  MissingImportJob: 'error',
  OperationFailed: 'error',
  Unknown: 'default',
};

const ACTION_LABELS: Record<string, string> = {
  Retry: 'Ponowienie',
  Approve: 'Zatwierdzenie',
  Skip: 'Pominięto',
  Fail: 'Błąd',
};

const IMPORT_JOB_STATUS_LABELS: Record<string, string> = {
  pending: 'Oczekuje',
  processing: 'Przetwarzanie',
  completed: 'Zakończono',
  failed: 'Błąd',
  duplicate: 'Duplikat',
  requiresreview: 'Wymaga weryfikacji',
  rejected: 'Odrzucony',
  abandoned: 'Porzucony',
  missing: 'Brak',
};

const WARNING_LABELS: Record<string, string> = {
  'manual-fixed skipped by default':
    'Pominięto ręcznie naprawione rekordy (domyślne zachowanie).',
};

const RESOLVED_RELATION_STATUSES = new Set([
  'resolved',
  'matched',
  'matchedbyksef',
  'matchedbycomposite',
]);

const UNRESOLVED_RELATION_STATUSES = new Set([
  'unresolved',
  'unmatched',
  'notresolved',
  'partiallymatched',
  'missingreferencedata',
  'nomatch',
  'nolocalmatch',
  'skippedmanualfixed',
  'statusnoteligible',
  'noteligible',
  'missingimportjob',
  'operationfailed',
  'unknown',
]);

const normalizeRelationStatus = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s_-]+/g, '');

export const formatFa3ReasonCodeLabel = (reasonCode?: string | null): string => {
  if (!reasonCode) {
    return REASON_CODE_LABELS.Unknown;
  }

  return REASON_CODE_LABELS[reasonCode] ?? reasonCode;
};

export const getFa3ReasonCodeColor = (reasonCode?: string | null): ChipColor => {
  if (!reasonCode) {
    return 'default';
  }

  return REASON_CODE_COLORS[reasonCode] ?? 'default';
};

export const formatFa3RelationStatusLabel = (relationStatus?: string | null): string => {
  if (!relationStatus) {
    return 'Nieznana';
  }

  const normalized = normalizeRelationStatus(relationStatus);

  if (UNRESOLVED_RELATION_STATUSES.has(normalized)) {
    return 'Nierozwiązana';
  }

  if (RESOLVED_RELATION_STATUSES.has(normalized)) {
    return 'Rozwiązana';
  }

  return relationStatus;
};

export const formatImportJobStatusLabel = (status?: string | null): string => {
  if (!status) {
    return '—';
  }

  const normalized = normalizeRelationStatus(status);
  return IMPORT_JOB_STATUS_LABELS[normalized] ?? status;
};

export const formatRetryApproveActionLabel = (action?: string | null): string => {
  if (!action) {
    return '—';
  }

  return ACTION_LABELS[action] ?? action;
};

export const formatRetryApproveMessageLabel = (message?: string | null): string => {
  if (!message) {
    return '—';
  }

  const trimmed = message.trim();
  if (!trimmed) {
    return '—';
  }

  if (/^Import job not found\.?$/i.test(trimmed)) {
    return 'Nie znaleziono zlecenia importu.';
  }

  const retryStatusMatch = trimmed.match(/^Retry returned status '([^']+)'\.?$/i);
  if (retryStatusMatch) {
    return `Ponowienie zwróciło status '${formatImportJobStatusLabel(retryStatusMatch[1])}'.`;
  }

  if (/^Status is not eligible for retry\/approve split flow\.?$/i.test(trimmed)) {
    return 'Status nie kwalifikuje się do ponowienia/zatwierdzenia w split-flow.';
  }

  const correlationMatch = trimmed.match(/^Operation failed\. CorrelationId=(.+?)(?:\.)?$/i);
  if (correlationMatch) {
    return `Operacja zakończona błędem. CorrelationId=${correlationMatch[1]}.`;
  }

  return trimmed;
};

export const formatFa3WarningLabel = (warning?: string | null): string => {
  if (!warning) {
    return '—';
  }

  const trimmed = warning.trim();
  if (!trimmed) {
    return '—';
  }

  const manualFixedOverrideMatch = trimmed.match(
    /^Manual-fixed override applied for invoice (\d+)\.?$/i
  );
  if (manualFixedOverrideMatch) {
    return `Zastosowano override manual-fixed dla faktury ${manualFixedOverrideMatch[1]}.`;
  }

  const normalized = trimmed.toLowerCase();
  return WARNING_LABELS[normalized] ?? trimmed;
};
